import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AppNotification,
  NotificationsService,
  translateNotification,
} from '../../services/notifications.service';
import { SupabaseService } from '../../services/supabase.service';

/**
 * Header notification bell for EVERY signed-in user (athletes get request
 * decisions; admins get incoming requests). State lives in
 * NotificationsService: the badge updates LIVE over Supabase Realtime — no
 * polling, no reload. Clicking a row routes admins to that club's requests
 * inbox and athletes to their own requests page.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    @if (signedIn) {
      <div class="nb">
        <button
          type="button"
          class="ap-nav__icon ap-focus nb__btn"
          [attr.aria-label]="
            unreadCount > 0
              ? ('notifications.ariaSome' | translate: { count: unreadCount })
              : ('notifications.ariaNone' | translate)
          "
          [attr.aria-expanded]="open"
          aria-haspopup="menu"
          (click)="toggle()"
        >
          <i class="pi pi-bell"></i>
          @if (unreadCount > 0) {
            <span class="nb__badge" aria-hidden="true">{{ badgeLabel }}</span>
          }
        </button>

        @if (open) {
          <div
            class="nb__panel"
            role="menu"
            [attr.aria-label]="'notifications.title' | translate"
          >
            <div class="nb__head">
              <span class="nb__title">{{ 'notifications.title' | translate }}</span>
              @if (hasUnread) {
                <button
                  type="button"
                  class="nb__markall ap-focus"
                  (click)="markAllRead()"
                >
                  {{ 'notifications.markAllRead' | translate }}
                </button>
              }
            </div>

            <div class="nb__list">
              @if (loading) {
                <div class="nb__loading">
                  <span class="nb__shimmer"></span>
                  <span class="nb__shimmer"></span>
                  <span class="nb__shimmer"></span>
                </div>
              } @else if (notifications.length === 0) {
                <div class="nb__empty">{{ 'notifications.empty' | translate }}</div>
              } @else {
                @for (n of notifications; track n.id) {
                  <button
                    type="button"
                    class="nb__item ap-focus"
                    [class.nb__item--unread]="!n.read"
                    role="menuitem"
                    (click)="onRowClick(n)"
                  >
                    <span class="nb__dot" [class.nb__dot--on]="!n.read" aria-hidden="true"></span>
                    <span class="nb__body">
                      <span class="nb__itemtitle">{{ display(n).title }}</span>
                      @if (n.body) {
                        <span class="nb__itembody">{{ display(n).body }}</span>
                      }
                    </span>
                    <span class="nb__time">{{ relativeTime(n.created_at) }}</span>
                  </button>
                }
              }
            </div>
          </div>
        }
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }
      .nb {
        position: relative;
        display: inline-flex;
      }
      .nb__btn {
        position: relative;
      }
      .nb__badge {
        position: absolute;
        top: 2px;
        right: 2px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        border-radius: 980px;
        background: var(--ap-green);
        color: #fff;
        font-size: 10px;
        font-weight: 600;
        line-height: 16px;
        text-align: center;
        box-shadow: 0 0 0 2px var(--ap-bg);
      }
      .nb__panel {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        width: 320px;
        max-width: calc(100vw - 24px);
        background: rgba(255, 255, 255, 0.92);
        -webkit-backdrop-filter: saturate(180%) blur(20px);
        backdrop-filter: saturate(180%) blur(20px);
        border: 1px solid var(--ap-hairline);
        border-radius: var(--ap-r-sm);
        box-shadow: var(--ap-shadow-float);
        overflow: hidden;
        z-index: var(--ap-z-dropdown);
      }
      .nb__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 14px;
        border-bottom: 1px solid var(--ap-hairline);
      }
      .nb__title {
        font-size: 15px;
        font-weight: 600;
        color: var(--ap-ink);
      }
      .nb__markall {
        border: 0;
        background: transparent;
        padding: 4px 6px;
        border-radius: 8px;
        font-family: inherit;
        font-size: 13px;
        font-weight: 500;
        color: var(--ap-green);
        cursor: pointer;
        transition: background-color 0.2s ease;
      }
      .nb__markall:hover {
        background: var(--ap-green-tint);
      }
      .nb__list {
        max-height: 360px;
        overflow-y: auto;
      }
      .nb__item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        width: 100%;
        padding: 11px 14px;
        border: 0;
        border-bottom: 1px solid var(--ap-hairline);
        background: transparent;
        text-align: left;
        cursor: pointer;
        transition: background-color 0.15s ease;
      }
      .nb__item:last-child {
        border-bottom: 0;
      }
      .nb__item:hover {
        background: rgba(0, 0, 0, 0.035);
      }
      .nb__item--unread {
        background: var(--ap-green-tint);
      }
      .nb__item--unread:hover {
        background: var(--ap-green-tint-hover, #dcefdf);
      }
      .nb__dot {
        flex-shrink: 0;
        width: 7px;
        height: 7px;
        margin-top: 6px;
        border-radius: 50%;
        background: transparent;
      }
      .nb__dot--on {
        background: var(--ap-green-bright, #2ecc71);
      }
      .nb__body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .nb__itemtitle {
        font-size: 14px;
        font-weight: 600;
        color: var(--ap-ink);
        line-height: 1.35;
      }
      .nb__itembody {
        font-size: 13px;
        color: var(--ap-ink-2);
        line-height: 1.4;
      }
      .nb__time {
        flex-shrink: 0;
        font-size: 12px;
        color: var(--ap-ink-3);
        white-space: nowrap;
        margin-top: 1px;
      }
      .nb__empty {
        padding: 28px 14px;
        text-align: center;
        font-size: 14px;
        color: var(--ap-ink-2);
      }
      .nb__loading {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 14px;
      }
      .nb__shimmer {
        height: 38px;
        border-radius: 10px;
        background: linear-gradient(
          90deg,
          rgba(0, 0, 0, 0.04) 25%,
          rgba(0, 0, 0, 0.07) 37%,
          rgba(0, 0, 0, 0.04) 63%
        );
        background-size: 400% 100%;
        animation: nb-shimmer 1.4s ease infinite;
      }
      @keyframes nb-shimmer {
        0% {
          background-position: 100% 50%;
        }
        100% {
          background-position: 0 50%;
        }
      }
    `,
  ],
})
export class NotificationBellComponent implements OnInit {
  private supabase = inject(SupabaseService);
  private host = inject(ElementRef<HTMLElement>);
  private router = inject(Router);
  private readonly translate = inject(TranslateService);

  /** Clubs the current user administers — used only to pick the click route. */
  adminClubIds: string[] = [];
  signedIn = false;
  open = false;

  private readonly notif = inject(NotificationsService);

  get unreadCount(): number {
    return this.notif.unreadCount();
  }
  get notifications(): AppNotification[] {
    return this.notif.latest();
  }
  get loading(): boolean {
    return false; // state is held (and live-updated) by the service
  }

  async ngOnInit(): Promise<void> {
    try {
      const user = await this.supabase.getCurrentUser();
      if (!user) return;
      this.signedIn = true;
      // Initial load + live realtime subscription (badge updates without reload).
      await this.notif.init();
      const clubs = await this.supabase.getUserClubs(user.id);
      this.adminClubIds = clubs
        .filter((c) => c.role === 'admin' || c.role === 'super_admin')
        .map((c) => c.club_id);
    } catch (e) {
      // Best-effort: a failed resolution simply leaves the bell hidden / empty.
      console.error('NotificationBell init failed:', e);
    }
  }

  /** "9+" cap for the badge label. */
  get badgeLabel(): string {
    return this.unreadCount > 9 ? '9+' : String(this.unreadCount);
  }

  get hasUnread(): boolean {
    return this.unreadCount > 0;
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) void this.notif.refresh();
  }

  close(): void {
    this.open = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.close();
  }

  /** Mark read + route: admins to that club's inbox, athletes to their requests. */
  async onRowClick(n: AppNotification): Promise<void> {
    void this.notif.markRead(n);
    this.close();
    if (n.club_id && this.adminClubIds.includes(n.club_id)) {
      void this.router.navigate(['/club', n.club_id, 'requests']);
    } else {
      void this.router.navigate(['/requests']);
    }
  }

  async markAllRead(): Promise<void> {
    await this.notif.markAllRead();
  }

  /** Rows arrive with English title/body from the DB triggers — map known ones. */
  display(n: AppNotification): { title: string; body: string | null } {
    return translateNotification(n, this.translate);
  }

  /**
   * Tiny relative-time helper (no date lib): "now", "2m", "3h", "5d", "2w",
   * then falls back to "MMM D" for older items.
   */
  relativeTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diffMs = Date.now() - then;
    const sec = Math.floor(diffMs / 1000);
    if (sec < 45) return this.translate.instant('notifications.now');
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d`;
    const wk = Math.floor(day / 7);
    if (wk < 5) return `${wk}w`;
    const d = new Date(then);
    return d.toLocaleDateString(this.translate.currentLang() || undefined, {
      month: 'short',
      day: 'numeric',
    });
  }
}
